import React from "react";
import { useId } from "react";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Upload } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PresignedImage } from "@/components/common/PresignedImage";
import { SortableList } from "@/components/generic/SortableList";
import { FeedbackReminderCard } from "./FeedbackReminderCard";
import type {
  PostCheckoutFeedbackChannel,
  PostCheckoutFeedbackReminder,
  PostCheckoutFeedbackSettings,
} from "@/types/booking-checkout-settings";

interface ReservationFeedbackSettingsFormProps {
  settings: PostCheckoutFeedbackSettings;
  isUploading: boolean;
  onSettingsChange: (settings: PostCheckoutFeedbackSettings) => void;
  onMediaUpload: (file: File) => void;
}

const channels: PostCheckoutFeedbackChannel[] = ["WHATSAPP", "APP", "EMAIL"];
const initialSendOptions = [1, 2, 3, 4, 6, 12, 24, 48];
const thresholdOptions = [1, 2, 3, 4, 5];

const createReminder = (order: number): PostCheckoutFeedbackReminder => ({
  clientId: nanoid(),
  order,
  sendAfterHours: 6,
  condition: "EMAIL_NOT_OPENED",
  isActive: true,
});

const reorderReminders = (
  reminders: PostCheckoutFeedbackReminder[],
): PostCheckoutFeedbackReminder[] =>
  reminders.map((reminder, index) => ({
    ...reminder,
    clientId: reminder.clientId ?? reminder.id ?? nanoid(),
    order: index + 1,
  }));

const getReminderKey = (reminder: PostCheckoutFeedbackReminder): string =>
  reminder.clientId ?? reminder.id ?? String(reminder.order);

export const ReservationFeedbackSettingsForm = ({
  settings,
  isUploading,
  onSettingsChange,
  onMediaUpload,
}: ReservationFeedbackSettingsFormProps): React.JSX.Element => {
  const { t } = useTranslation();
  const uploadInputId = useId();

  const handleChannelChange = (
    channel: PostCheckoutFeedbackChannel,
    checked: boolean,
  ): void => {
    const nextChannels = checked
      ? [...settings.channels, channel]
      : settings.channels.filter((item) => item !== channel);

    onSettingsChange({
      ...settings,
      channels: nextChannels,
    });
  };

  const handleReminderChange = (
    index: number,
    reminder: PostCheckoutFeedbackReminder,
  ): void => {
    const reminders = settings.reminders.map((item, reminderIndex) =>
      reminderIndex === index ? reminder : item,
    );
    onSettingsChange({
      ...settings,
      reminders: reorderReminders(reminders),
    });
  };

  const handleReminderRemove = (index: number): void => {
    onSettingsChange({
      ...settings,
      reminders: reorderReminders(
        settings.reminders.filter(
          (_, reminderIndex) => reminderIndex !== index,
        ),
      ),
    });
  };

  const handleReminderAdd = (): void => {
    onSettingsChange({
      ...settings,
      reminders: reorderReminders([
        ...settings.reminders,
        createReminder(settings.reminders.length + 1),
      ]),
    });
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    onMediaUpload(file);
    event.target.value = "";
  };

  return (
    <div className="flex flex-col gap-5">
      <Label className="flex items-start gap-3">
        <Switch
          checked={settings.enabled}
          onCheckedChange={(enabled) =>
            onSettingsChange({
              ...settings,
              enabled,
            })
          }
        />
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium">
            {t("feedbacks.settings.form.enabled")}
          </span>
        </div>
      </Label>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          {t("feedbacks.settings.form.photo")}
        </span>
        <div className="flex flex-wrap gap-3">
          <Label className="relative h-32 w-64 cursor-pointer overflow-hidden rounded-lg bg-muted">
            {settings.mediaId ? (
              <>
                <PresignedImage
                  fileId={settings.mediaId}
                  alt={t("feedbacks.settings.preview.feedbackPhotoAlt")}
                  className="size-full object-cover"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute right-2 top-2 size-8 bg-background/90"
                  onClick={(event) => {
                    event.preventDefault();
                    onSettingsChange({
                      ...settings,
                      mediaId: null,
                    });
                  }}
                >
                  <Trash2 className="size-4" />
                  <span className="sr-only">
                    {t("feedbacks.settings.form.removePhoto")}
                  </span>
                </Button>
              </>
            ) : (
              <span className="flex size-full items-center justify-center text-sm text-muted-foreground">
                {t("feedbacks.settings.form.photoPlaceholder")}
              </span>
            )}
            <input
              id={uploadInputId}
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={isUploading}
              onChange={handleFileChange}
            />
          </Label>
          <Label
            htmlFor={uploadInputId}
            className="flex h-32 w-64 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border bg-background text-muted-foreground transition-colors hover:bg-muted/40"
          >
            <Upload className="size-5" />
            <span className="sr-only">
              {t("feedbacks.settings.form.uploadPhoto")}
            </span>
          </Label>
        </div>
      </div>

      <Label className="flex flex-col gap-2 text-sm font-medium">
        {t("feedbacks.settings.form.initialSendAfter")}
        <Select
          value={String(settings.initialSendAfterHours)}
          onValueChange={(value) =>
            onSettingsChange({
              ...settings,
              initialSendAfterHours: Number(value),
            })
          }
        >
          <SelectTrigger className="h-10 w-full justify-between">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {initialSendOptions.map((hours) => (
                <SelectItem key={hours} value={String(hours)}>
                  {t("feedbacks.settings.form.hours", { count: hours })}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Label>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          {t("feedbacks.settings.form.channels")}
        </legend>
        <div className="flex flex-wrap gap-5">
          {channels.map((channel) => (
            <Label key={channel} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={settings.channels.includes(channel)}
                onCheckedChange={(checked) =>
                  handleChannelChange(channel, checked === true)
                }
              />
              {t(`feedbacks.settings.form.channelOptions.${channel}`)}
            </Label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          {t("feedbacks.settings.form.reminders")}
        </span>
        <SortableList
          items={settings.reminders}
          direction="column"
          keyExtractor={getReminderKey}
          onReorder={(reminders) =>
            onSettingsChange({
              ...settings,
              reminders: reorderReminders(reminders),
            })
          }
          className="flex flex-col gap-3"
          renderItem={(reminder) => {
            const index = settings.reminders.findIndex(
              (item) => getReminderKey(item) === getReminderKey(reminder),
            );

            return (
              <FeedbackReminderCard
                reminder={reminder}
                canRemove={settings.reminders.length > 1}
                onChange={(nextReminder) =>
                  handleReminderChange(index, nextReminder)
                }
                onRemove={() => handleReminderRemove(index)}
              />
            );
          }}
        />
        <div className="rounded-lg border border-border bg-muted/30 p-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9"
            onClick={handleReminderAdd}
          >
            <Plus className="size-4" />
            <span className="sr-only">
              {t("feedbacks.settings.form.addReminder")}
            </span>
          </Button>
        </div>
      </div>

      <Label className="flex items-center gap-3 text-sm font-medium">
        <Switch
          checked={settings.externalReviewsEnabled}
          onCheckedChange={(checked) =>
            onSettingsChange({
              ...settings,
              externalReviewsEnabled: checked,
            })
          }
        />
        {t("feedbacks.settings.form.externalReviewsEnabled")}
      </Label>

      {settings.externalReviewsEnabled && (
        <div className="grid gap-4 md:grid-cols-2">
          <Label className="flex flex-col gap-2 text-sm font-medium">
            {t("feedbacks.settings.form.bookingReviewUrl")}
            <Input
              value={settings.bookingReviewUrl ?? ""}
              placeholder={t("feedbacks.settings.form.bookingReviewUrl")}
              onChange={(event) =>
                onSettingsChange({
                  ...settings,
                  bookingReviewUrl: event.target.value,
                })
              }
            />
          </Label>
          <Label className="flex flex-col gap-2 text-sm font-medium">
            {t("feedbacks.settings.form.googleReviewUrl")}
            <Input
              value={settings.googleReviewUrl ?? ""}
              placeholder={t("feedbacks.settings.form.urlPlaceholder")}
              onChange={(event) =>
                onSettingsChange({
                  ...settings,
                  googleReviewUrl: event.target.value,
                })
              }
            />
          </Label>
          <Label className="flex flex-col gap-2 text-sm font-medium md:col-span-2">
            {t("feedbacks.settings.form.lowRatingThreshold")}
            <Select
              value={String(settings.lowRatingThreshold)}
              onValueChange={(value) =>
                onSettingsChange({
                  ...settings,
                  lowRatingThreshold: Number(value),
                })
              }
            >
              <SelectTrigger className="h-10 w-full justify-between">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {thresholdOptions.map((stars) => (
                    <SelectItem key={stars} value={String(stars)}>
                      {t("feedbacks.settings.form.stars", { count: stars })}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Label>
        </div>
      )}
    </div>
  );
};
